/**
 * Phase 2Z.ae - one-line employee skills hint.
 *
 * Renders a very small, subtle list of an employee's skill names so a
 * manager can see context like "English, Spanish, Salesforce..." under
 * the employee name on the roster and on the draft review page.
 *
 * Rules baked in here so every consumer behaves the same way:
 *   - Cap at MAX (default 3) skill names.
 *   - When there are extra skills, show "+N" at the end.
 *   - Skip names that aren't in the lookup map (so deleted/archived
 *     skills don't render as "undefined").
 *   - Return nothing when there are zero usable skills - keeps layouts
 *     unchanged for employees with no skills set.
 *
 * No fake data, no fetching, no backend work - parent components pass
 * the skill_ids array and a {skill_id -> name} lookup they already have.
 */
const MAX = 3;

export default function EmployeeSkillsLine({
  skillIds,
  skillNameById,
  max = MAX,
  className = "",
  testidPrefix,
}) {
  if (!Array.isArray(skillIds) || skillIds.length === 0) return null;
  if (!skillNameById) return null;

  const names = skillIds
    .map((id) => skillNameById[id])
    .filter((n) => typeof n === "string" && n.length > 0);
  if (names.length === 0) return null;

  const shown = names.slice(0, max);
  const overflow = Math.max(0, names.length - shown.length);

  return (
    <div
      className={`text-[11px] text-[#94A3B8] truncate ${className}`}
      data-testid={testidPrefix ? `${testidPrefix}-skills` : undefined}
      title={names.join(", ")}
    >
      {shown.join(", ")}
      {overflow > 0 && (
        <span className="ml-1 text-[#64748B]" data-testid={testidPrefix ? `${testidPrefix}-skills-overflow` : undefined}>
          +{overflow}
        </span>
      )}
    </div>
  );
}
